import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type Board, type Clip } from '@air/api/types';
import { hydrateSelectedItemsAction } from '@air/redux-selected-items';

import { GalleryItemType } from '~/components/Gallery/types';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { setSelectableGalleryItemsAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';

/**
 * One section of a gallery's selectable items, tagged with the kind of item it holds. A LIST of
 * these is what registration takes, because the order of the flattened result is the order
 * shift-range selection walks — so a surface whose sections are user-reorderable (the showcase's
 * content blocks) has to state its own order rather than accept a fixed one.
 */
export type GallerySelectableSection<B extends Board, C extends Clip> =
  | { type: GalleryItemType.board; items: B[] }
  | { type: GalleryItemType.asset; items: C[] }
  | { type: GalleryItemType.file; items: C[] };

const toSelectableGalleryItems = <B extends Board, C extends Clip>(
  section: GallerySelectableSection<B, C>,
): SelectableGalleryItem[] => {
  if (section.type === GalleryItemType.board) {
    return section.items.map((item) => ({ id: item.id, type: GalleryItemType.board, item }));
  }

  if (section.type === GalleryItemType.asset) {
    return section.items.map((item) => ({ id: item.id, type: GalleryItemType.asset, item }));
  }

  return section.items.map((item) => ({ id: item.id, type: GalleryItemType.file, item }));
};

/**
 * Registers the rendered items as the selectable set, IN THE ORDER the sections are given — the
 * reducer treats that order as visual order when resolving a SHIFT-click range, so it has to match
 * what the viewer sees.
 *
 * Pass a memoized `sections` array: it is an effect dependency, and a fresh array every render
 * re-dispatches the whole set on every render.
 */
export function useOrderedGallerySelectedItems<B extends Board, C extends Clip>(
  sections: GallerySelectableSection<B, C>[],
  supportsPersistence = false,
) {
  const dispatch = useDispatch();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
  const currentBoardId = useSelector(centralizedBoardIdSelector);

  useEffect(() => {
    const persist = supportsPersistence || (selectAll && includeNestedItems);

    dispatch(
      setSelectableGalleryItemsAction({
        selectableItems: sections.flatMap(toSelectableGalleryItems),
        persist,
      }),
    );

    // When selectAll is true at root board, select only assets and files (not boards)
    // Don't re-hydrate on navigation - selection persists from root board
    const isAtRootBoard = !selectAllRootBoardId || currentBoardId === selectAllRootBoardId;

    if (selectAll && isAtRootBoard) {
      dispatch(
        hydrateSelectedItemsAction({
          selectedItems: sections
            .filter((section) => section.type !== GalleryItemType.board)
            .flatMap(toSelectableGalleryItems),
        }),
      );
    }
  }, [dispatch, selectAll, includeNestedItems, selectAllRootBoardId, currentBoardId, sections, supportsPersistence]);
}

/**
 * Registration for a gallery whose sections are always boards → assets → files, which is every
 * surface that renders ONE gallery. A surface rendering several (the showcase's content blocks)
 * registers their combined items through `useOrderedGallerySelectedItems` instead, so the order
 * follows the blocks.
 */
export function useGroupedGallerySelectedItems<B extends Board, C extends Clip>(
  boards: B[],
  assets: C[],
  files: C[],
  supportsPersistence = false,
) {
  const sections = useMemo<GallerySelectableSection<B, C>[]>(
    () => [
      { type: GalleryItemType.board, items: boards },
      { type: GalleryItemType.asset, items: assets },
      { type: GalleryItemType.file, items: files },
    ],
    [boards, assets, files],
  );

  useOrderedGallerySelectedItems(sections, supportsPersistence);
}
